import { faFileText, faClock, faEdit, faEye } from "@fortawesome/free-regular-svg-icons";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type React from "react";
import { useState, useEffect, useRef } from "react";
import { parseLrcFile, type TimedLine } from "../utils/lyricsParser";

interface RightSidebarProps {
  currentLyrics: string;
  setCurrentLyrics: (lyrics: string) => void;
  timedLines?: TimedLine[];
  setTimedLines?: (lines: TimedLine[]) => void;
  onSeekToTime?: (time: number) => void;
  currentTime?: number;
  currentLineIndex?: number;
}

function RightSidebar({
  currentLyrics,
  setCurrentLyrics,
  timedLines,
  setTimedLines,
  onSeekToTime,
  currentTime = 0,
  currentLineIndex = -1,
}: RightSidebarProps): React.JSX.Element {
  const [parsedLrcLines, setParsedLrcLines] = useState<TimedLine[]>([]);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [rawLrcContent, setRawLrcContent] = useState<string>(currentLyrics);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Sync rawLrcContent with currentLyrics when it changes from outside
  useEffect(() => {
    setRawLrcContent(currentLyrics);
    setHasUnsavedChanges(false);
  }, [currentLyrics]);

  // Function to detect if content is LRC format
  const isLrcFormat = (text: string): boolean => {
    const lines = text.split('\n');
    const lrcLines = lines.filter(line => {
      const trimmed = line.trim();
      return trimmed && /^\[\d{2}:\d{2}\.\d{2}\]/.test(trimmed);
    });
    return lrcLines.length > 0 && lrcLines.length >= lines.length * 0.5; // At least 50% of lines are LRC format
  };

  // Parse LRC content when textarea changes
  const handleLyricsChange = (newLyrics: string): void => {
    // Only update local state in edit mode, don't trigger parsing yet
    setRawLrcContent(newLyrics);
    setHasUnsavedChanges(true);
  };

  const handleSave = (): void => {
    // Save changes and parse
    setCurrentLyrics(rawLrcContent);
    
    if (isLrcFormat(rawLrcContent)) {
      try {
        const parsed = parseLrcFile(rawLrcContent);
        setParsedLrcLines(parsed);
        setTimedLines?.(parsed); // Update parent's timedLines
        console.log('Parsed LRC from textarea:', parsed.length, 'lines');
      } catch (error) {
        console.error('Failed to parse LRC from textarea:', error);
        setParsedLrcLines([]);
        setTimedLines?.([]); // Clear parent's timedLines
      }
    } else {
      setParsedLrcLines([]);
      setTimedLines?.([]); // Clear parent's timedLines
    }
    
    setHasUnsavedChanges(false);
  };

  const handleCancel = (): void => {
    // Revert to original content
    setRawLrcContent(currentLyrics);
    setHasUnsavedChanges(false);
  };


  // Generate raw LRC content from parsed lines for display in edit mode
  const getRawLrcContent = (): string => {
    if (parsedLrcLines.length > 0) {
      return parsedLrcLines.map(line => {
        const minutes = Math.floor(line.time / 60);
        const seconds = line.time % 60;
        const timestamp = `[${minutes.toString().padStart(2, '0')}:${seconds.toFixed(2).padStart(5, '0')}]`;
        return `${timestamp}${line.text}`;
      }).join('\n');
    }
    return currentLyrics;
  };

  // Functions to navigate to next/previous lines
  const goToPreviousLine = (): void => {
    const lines = displayTimedLines;
    if (lines && lines.length > 0 && currentLineIndex > 0) {
      const targetLineIndex = currentLineIndex - 1;
      const previousLineTime = lines[targetLineIndex].time;
      console.log(`RightSidebar: Going to previous line ${targetLineIndex + 1} at time ${previousLineTime}`);
      onSeekToTime?.(previousLineTime);
    }
  };

  const goToNextLine = (): void => {
    const lines = displayTimedLines;
    if (lines && lines.length > 0 && currentLineIndex < lines.length - 1) {
      const targetLineIndex = currentLineIndex + 1;
      const nextLineTime = lines[targetLineIndex].time;
      console.log(`RightSidebar: Going to next line ${targetLineIndex + 1} at time ${nextLineTime}`);
      onSeekToTime?.(nextLineTime);
    }
  };

  // Use parsed LRC lines if available, otherwise use prop timedLines
  const displayTimedLines = parsedLrcLines.length > 0 ? parsedLrcLines : timedLines;

  // Function to scroll to a specific line
  const scrollToLine = (lineIndex: number): void => {
    if (!scrollContainerRef.current || lineIndex < 0) return;
    
    // Wait for DOM to be ready
    setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      
      // Find the inner container that holds the actual line elements
      const innerContainer = container.children[0] as HTMLElement;
      if (!innerContainer) return;
      
      // Find the line element by its index within the inner container
      const lineElement = innerContainer.children[lineIndex] as HTMLElement;
      if (lineElement) {
        const containerRect = container.getBoundingClientRect();
        const lineRect = lineElement.getBoundingClientRect();
        
        // Calculate the line's position relative to the container
        const lineTop = lineRect.top - containerRect.top + container.scrollTop;
        
        // Position the line at 1/3 from the top for better visibility
        const targetScrollTop = lineTop - containerRect.height / 3;
        
        console.log('RightSidebar: Scrolling to line', lineIndex, 'at position', targetScrollTop);
        
        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      } else {
        console.log('RightSidebar: Line element not found for index', lineIndex);
      }
    }, 50); // Small delay to ensure DOM is ready
  };

  // Auto-scroll to current line when it changes
  useEffect(() => {
    if (currentLineIndex >= 0 && !isEditMode && displayTimedLines && displayTimedLines.length > 0) {
      console.log('RightSidebar: Auto-scrolling to line', currentLineIndex);
      scrollToLine(currentLineIndex);
    }
  }, [currentLineIndex, isEditMode, displayTimedLines]);

  // Backup auto-scroll on currentTime changes (in case currentLineIndex doesn't update)
  useEffect(() => {
    if (!isEditMode && displayTimedLines && displayTimedLines.length > 0) {
      // Calculate current line index based on time using the same logic as App.tsx
      let calculatedLineIndex = -1;
      for (let i = 0; i < displayTimedLines.length; i++) {
        const currentLineTime = displayTimedLines[i].time;
        const nextLineTime = i < displayTimedLines.length - 1 ? displayTimedLines[i + 1].time : Infinity;
        
        // Current line is active if time is >= current line time and < next line time
        // Add small tolerance for floating point precision issues
        const tolerance = 0.001;
        if (currentTime >= currentLineTime - tolerance && currentTime < nextLineTime - tolerance) {
          calculatedLineIndex = i;
          break;
        }
      }
      
      // If we're past all lines, use the last line
      if (calculatedLineIndex === -1 && displayTimedLines.length > 0) {
        calculatedLineIndex = displayTimedLines.length - 1;
      }
      
      if (calculatedLineIndex >= 0 && calculatedLineIndex !== currentLineIndex) {
        console.log('RightSidebar: Backup auto-scroll to calculated line', calculatedLineIndex);
        scrollToLine(calculatedLineIndex);
      }
    }
  }, [currentTime, isEditMode, displayTimedLines, currentLineIndex]);
  return (
    <aside className="sidebar">
      <div className="flex-1 flex flex-col min-h-0">
        <div className="p-4 border-b border-border-primary">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faFileText} className="text-primary w-5 h-5" />
              <h3 className="card-title">Lyrics</h3>
            </div>
            <div className="flex gap-1">
              <button
                className={isEditMode ? 'nav-item-active' : 'nav-item-inactive'}
                onClick={() => setIsEditMode(true)}
                title="Edit Mode"
              >
                <FontAwesomeIcon icon={faEdit} />
                <span>Edit</span>
              </button>
              <button
                className={!isEditMode ? 'nav-item-active' : 'nav-item-inactive'}
                onClick={() => setIsEditMode(false)}
                title="Display Mode"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Display</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0 p-4">
          {isEditMode ? (
            <div className="flex-1 flex flex-col gap-2">
              <textarea
                value={rawLrcContent}
                onChange={(e) => handleLyricsChange(e.target.value)}
                placeholder="Enter your lyrics here or paste LRC content..."
                className="flex-1 w-full px-4 py-2 bg-slate-700 text-slate-100 border border-slate-600 rounded-md focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-none font-mono text-sm leading-relaxed"
              />
              <div className="flex gap-2 justify-end">
                <button
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border-none rounded-md font-medium text-sm cursor-pointer transition-all text-slate-100 bg-slate-600 hover:bg-slate-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleCancel}
                  disabled={!hasUnsavedChanges}
                >
                  Cancel
                </button>
                <button
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border-none rounded-md font-medium text-sm cursor-pointer transition-all text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            displayTimedLines && displayTimedLines.length > 0 ? (
              <div ref={scrollContainerRef} className="flex-1 overflow-y-auto smooth-scroll">
                <div className="space-y-1">
                  {displayTimedLines.map((line, index) => {
                    const isCurrentLine = index === currentLineIndex;
                    return (
                    <div key={index} className={`flex items-center gap-3 p-2 rounded-md transition-colors relative ${
                      isCurrentLine 
                        ? 'bg-blue-500/20 border border-blue-500/40' 
                        : 'hover:bg-slate-600'
                    }`}>
                      {index > 0 && (
                        <div className="absolute top-0 left-0 right-0 h-px bg-slate-600"></div>
                      )}
                      <button
                        className="flex items-center gap-1 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-xs text-slate-400 hover:text-slate-100 hover:bg-slate-500 transition-all flex-shrink-0"
                        onClick={() => onSeekToTime?.(line.time)}
                        title={`Seek to ${Math.floor(line.time / 60).toString().padStart(2, '0')}:${(line.time % 60).toFixed(2).padStart(5, '0')}`}
                      >
                        <FontAwesomeIcon icon={faClock} />
                        <span className="font-mono">
                          {Math.floor(line.time / 60).toString().padStart(2, '0')}:{(line.time % 60).toFixed(2).padStart(5, '0')}
                        </span>
                      </button>
                      <div className="text-slate-100 text-sm leading-relaxed flex-1 text-center">
                        {line.text}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-center text-slate-400">No LRC content detected. Switch to Edit Mode to paste LRC content.</p>
              </div>
            )
          )}
        </div>
        
        {/* Navigation buttons footer */}
        {displayTimedLines && displayTimedLines.length > 0 && !isEditMode && (
          <div className="bg-slate-800 border-t border-slate-600 px-4 py-3">
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={goToPreviousLine}
                disabled={currentLineIndex <= 0}
                className={`inline-flex items-center gap-1 px-3 py-1.5 border-none rounded-md text-xs font-medium cursor-pointer transition-all ${
                  currentLineIndex <= 0
                    ? 'text-slate-500 bg-slate-700 cursor-not-allowed'
                    : 'text-slate-100 bg-slate-600 hover:bg-slate-500'
                }`}
                title="Previous line"
              >
                <FontAwesomeIcon icon={faChevronUp} />
                <span>Prev</span>
              </button>
              
              <div className="text-slate-400 text-xs font-medium">
                {currentLineIndex + 1} / {displayTimedLines.length}
              </div>
              
              <button
                type="button"
                onClick={goToNextLine}
                disabled={currentLineIndex >= displayTimedLines.length - 1}
                className={`inline-flex items-center gap-1 px-3 py-1.5 border-none rounded-md text-xs font-medium cursor-pointer transition-all ${
                  currentLineIndex >= displayTimedLines.length - 1
                    ? 'text-slate-500 bg-slate-700 cursor-not-allowed'
                    : 'text-slate-100 bg-slate-600 hover:bg-slate-500'
                }`}
                title="Next line"
              >
                <span>Next</span>
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

export default RightSidebar;
